// packages/main-app/src/stores/globalStore.ts
import { create } from "zustand";
import { persist, createJSONStorage, devtools } from "zustand/middleware";
import actions from "../utils/globalState";
import i18n, {
  getStoredLanguage,
  type LanguageType,
} from "@eweadn-qiankun/shared-i18n";
import {
  applyTheme,
  setStoredTheme,
  THEME_STORAGE_KEY,
  type ThemeType,
} from "@eweadn-qiankun/shared-theme";
import type { SdkApiType } from "../types/sdkApi";
import type { UserInfo } from "../api/user";
import type { DeviceType } from "@eweadn-qiankun/shared-utils";
import {
  migrateWallpaperPreferenceFromGlobalStore,
  writeWallpaperPreference,
  type WallpaperMediaType,
  type WallpaperSource,
} from "../utils/wallpaperPreference";
import { computeWallpaperComponentBgTransparency } from "../utils/componentSurfaceBgTransparency";

export type { WallpaperMediaType, WallpaperSource };

export interface WallpaperImageOptions {
  source?: WallpaperSource;
  id?: string | number | null;
  mediaType?: WallpaperMediaType;
  /** 选中壁纸时按历史 25% 表面规则写入组件背景透明度 */
  applyLegacyComponentBgTransparency?: boolean;
}

interface PersistedGlobalStoreState {
  theme?: ThemeType;
  language?: LanguageType;
  wallpaperEnabled?: boolean;
  wallpaperUrl?: string;
  wallpaperSource?: WallpaperSource;
  wallpaperId?: string | number | null;
  wallpaperMediaType?: WallpaperMediaType;
  wallpaperBlur?: number;
  wallpaperBrightness?: number;
  wallpaperOpacity?: number;
  wallpaperComponentBgTransparency?: number;
}

const getPersistedGlobalStoreState = (): PersistedGlobalStoreState => {
  try {
    const rawStore = localStorage.getItem("global-store");
    if (!rawStore) return {};
    const store = JSON.parse(rawStore);
    return store?.state && typeof store.state === "object" ? store.state : {};
  } catch {
    return {};
  }
};

const persistedGlobalStoreState = getPersistedGlobalStoreState();
// 壁纸偏好以独立 key 为准，避免 zustand persist / HMR 回写旧值
const wallpaperPreference = migrateWallpaperPreferenceFromGlobalStore();

/** 同步壁纸偏好到独立 localStorage，作为刷新恢复的权威来源 */
const syncWallpaperPreference = (state: {
  wallpaperEnabled: boolean;
  wallpaperUrl: string;
  wallpaperSource: WallpaperSource;
  wallpaperId: string | number | null;
  wallpaperMediaType: WallpaperMediaType;
  wallpaperBlur: number;
  wallpaperBrightness: number;
  wallpaperOpacity: number;
  wallpaperComponentBgTransparency: number;
}) => {
  writeWallpaperPreference({
    wallpaperEnabled: state.wallpaperEnabled,
    wallpaperUrl: state.wallpaperUrl,
    wallpaperSource: state.wallpaperSource,
    wallpaperId: state.wallpaperId,
    wallpaperMediaType: state.wallpaperMediaType,
    wallpaperBlur: state.wallpaperBlur,
    wallpaperBrightness: state.wallpaperBrightness,
    wallpaperOpacity: state.wallpaperOpacity,
    wallpaperComponentBgTransparency: state.wallpaperComponentBgTransparency,
  });
};

interface GlobalStore {
  theme: ThemeType;
  language: LanguageType;
  sdkApi: SdkApiType | null;
  userInfo: UserInfo | null;
  token: string | null;
  isLoggedIn: boolean;
  currentDeviceType: DeviceType | "";
  currentDeviceName: string;
  currentDeviceSystem: number;
  equipmentData: object | null;
  authorizedDevices: any;

  wallpaperEnabled: boolean;
  wallpaperUrl: string;
  wallpaperSource: WallpaperSource;
  wallpaperId: string | number | null;
  wallpaperMediaType: WallpaperMediaType;
  wallpaperBlur: number;
  wallpaperBrightness: number;
  wallpaperOpacity: number;
  wallpaperComponentBgTransparency: number;

  setTheme: (theme: ThemeType) => void;
  setLanguage: (language: LanguageType) => void;
  setSdkApi: (sdkApi: SdkApiType) => void;
  setUserInfo: (userInfo: UserInfo | null) => void;
  setToken: (token: string | null) => void;
  setCurrentDeviceType: (deviceType: DeviceType | "") => void;
  setCurrentDeviceSystem: (system: number) => void;
  setCurrentDeviceName: (deviceName: string) => void;
  login: (token: string, userInfo: UserInfo) => void;
  logout: () => void;
  setEquipmentData: (equipment: object | null) => void;
  setAuthorizedDevices: (devices: any) => void;

  setWallpaperEnabled: (enabled: boolean) => void;
  setWallpaperImage: (url: string, options?: WallpaperImageOptions) => void;
  setWallpaperBlur: (blur: number) => void;
  setWallpaperBrightness: (brightness: number) => void;
  setWallpaperOpacity: (opacity: number) => void;
  setWallpaperComponentBgTransparency: (transparency: number) => void;
}

export const useGlobalStore = create<GlobalStore>()(
  devtools(
    persist(
      (set) => ({
        theme:
          (localStorage.getItem("app-theme") as ThemeType) ||
          persistedGlobalStoreState.theme ||
          "light",
        language: persistedGlobalStoreState.language || getStoredLanguage(),
        sdkApi: null,
        userInfo: JSON.parse(localStorage.getItem("userInfo") || "null"),
        token: localStorage.getItem("token"),
        isLoggedIn: !!localStorage.getItem("token"),
        currentDeviceType: "",
        currentDeviceName: "",
        currentDeviceSystem: 0,
        equipmentData: null,
        authorizedDevices: [],

        wallpaperEnabled: wallpaperPreference.wallpaperEnabled,
        wallpaperUrl: wallpaperPreference.wallpaperUrl,
        wallpaperSource: wallpaperPreference.wallpaperSource,
        wallpaperId: wallpaperPreference.wallpaperId,
        wallpaperMediaType: wallpaperPreference.wallpaperMediaType,
        wallpaperBlur: wallpaperPreference.wallpaperBlur,
        wallpaperBrightness: wallpaperPreference.wallpaperBrightness,
        wallpaperOpacity: wallpaperPreference.wallpaperOpacity,
        wallpaperComponentBgTransparency:
          wallpaperPreference.wallpaperComponentBgTransparency,

        setTheme: (theme) => {
          console.log("[Zustand] 设置主题:", theme);
          applyTheme(theme);
          setStoredTheme(theme);
          actions.setGlobalState({ theme });
          set({ theme });
        },

        setLanguage: (language) => {
          // 先广播给子应用，再切换主应用 i18n（避免 changeLanguage 异步导致子应用收不到）
          actions.setGlobalState({ language });
          void i18n.changeLanguage(language).then(() => {
            set({ language });
          });
        },

        setSdkApi: (sdkApi) => {
          set({ sdkApi });
        },

        setUserInfo: (userInfo) => {
          if (userInfo) {
            localStorage.setItem("userInfo", JSON.stringify(userInfo));
          } else {
            localStorage.removeItem("userInfo");
          }
          actions.setGlobalState({ userInfo });
          set({ userInfo });
        },

        setToken: (token) => {
          if (token) {
            localStorage.setItem("token", token);
          } else {
            localStorage.removeItem("token");
          }
          set({ token, isLoggedIn: !!token });
        },

        setCurrentDeviceType: (deviceType) => {
          actions.setGlobalState({ deviceType });
          set({ currentDeviceType: deviceType });
        },

        setCurrentDeviceSystem: (system) => {
          actions.setGlobalState({ system });
          set({ currentDeviceSystem: system });
        },

        setCurrentDeviceName: (deviceName) => {
          actions.setGlobalState({ deviceName });
          set({ currentDeviceName: deviceName });
        },

        login: (token, userInfo) => {
          localStorage.setItem("token", token);
          localStorage.setItem("userInfo", JSON.stringify(userInfo));
          actions.setGlobalState({ userInfo, token });
          set({ token, userInfo, isLoggedIn: true });
        },

        logout: () => {
          localStorage.removeItem("token");
          localStorage.removeItem("userInfo");
          actions.setGlobalState({ userInfo: null, token: null });
          set({ token: null, userInfo: null, isLoggedIn: false });
        },

        setEquipmentData: (equipment) => {
          actions.setGlobalState({ equipment });
          set({ equipmentData: equipment });
        },

        setAuthorizedDevices: (authorizedDevices: any) => {
          actions.setGlobalState({ authorizedDevices });
          set({ authorizedDevices });
        },

        setWallpaperEnabled: (enabled) => {
          const state = useGlobalStore.getState();
          const { wallpaperUrl } = state;
          const nextEnabled = enabled && !!wallpaperUrl;
          actions.setGlobalState({
            wallpaperEnabled: nextEnabled,
          });
          // DOM 同步统一由 useWallpaperSync 负责，避免与 store 内 apply 重复打断过渡动画
          set({ wallpaperEnabled: enabled });
          syncWallpaperPreference({
            ...useGlobalStore.getState(),
            wallpaperEnabled: enabled,
          });
        },

        setWallpaperImage: (url, options) => {
          const state = useGlobalStore.getState();
          const safeUrl = typeof url === "string" ? url : "";
          const { wallpaperSource, wallpaperId } = state;
          const hasSourceOption = Boolean(
            options && Object.prototype.hasOwnProperty.call(options, "source")
          );
          const hasIdOption = Boolean(
            options && Object.prototype.hasOwnProperty.call(options, "id")
          );
          const hasMediaTypeOption = Boolean(
            options &&
            Object.prototype.hasOwnProperty.call(options, "mediaType")
          );
          // 调用方显式传入 id/source 时必须生效，避免 ?? 回落到旧 id 导致刷新后按旧壁纸恢复
          const nextSource = safeUrl
            ? hasSourceOption
              ? (options?.source ?? "")
              : wallpaperSource
            : hasSourceOption
              ? (options?.source ?? "")
              : "";
          const nextId = safeUrl
            ? hasIdOption
              ? (options?.id ?? null)
              : wallpaperId
            : hasIdOption
              ? (options?.id ?? null)
              : null;
          const nextMediaType = safeUrl
            ? hasMediaTypeOption
              ? (options?.mediaType ?? "image")
              : state.wallpaperMediaType
            : "image";
          const nextEnabled = Boolean(safeUrl);
          let nextComponentBgTransparency =
            state.wallpaperComponentBgTransparency;
          if (!safeUrl) {
            nextComponentBgTransparency = 100;
          } else if (options?.applyLegacyComponentBgTransparency) {
            nextComponentBgTransparency =
              computeWallpaperComponentBgTransparency(state.wallpaperOpacity);
          }
          actions.setGlobalState({
            wallpaperEnabled: nextEnabled,
          });
          const nextState = {
            wallpaperUrl: safeUrl,
            wallpaperSource: nextSource,
            wallpaperId: nextId,
            wallpaperMediaType: nextMediaType,
            wallpaperEnabled: nextEnabled,
            wallpaperBlur: state.wallpaperBlur,
            wallpaperBrightness: state.wallpaperBrightness,
            wallpaperOpacity: state.wallpaperOpacity,
            wallpaperComponentBgTransparency: nextComponentBgTransparency,
          };
          // 先写独立偏好，再更新 store，确保刷新一定能读到最新选择
          syncWallpaperPreference(nextState);
          // DOM 同步统一由 useWallpaperSync 负责；选中/清空时与 enabled 一并持久化
          set({
            wallpaperUrl: safeUrl,
            wallpaperSource: nextSource,
            wallpaperId: nextId,
            wallpaperMediaType: nextMediaType,
            wallpaperEnabled: nextEnabled,
            wallpaperComponentBgTransparency: nextComponentBgTransparency,
          });
        },

        setWallpaperBlur: (blur) => {
          // DOM 同步统一由 useWallpaperSync 负责
          set({ wallpaperBlur: blur });
          syncWallpaperPreference({
            ...useGlobalStore.getState(),
            wallpaperBlur: blur,
          });
        },

        setWallpaperBrightness: (brightness) => {
          // DOM 同步统一由 useWallpaperSync 负责
          set({ wallpaperBrightness: brightness });
          syncWallpaperPreference({
            ...useGlobalStore.getState(),
            wallpaperBrightness: brightness,
          });
        },

        setWallpaperOpacity: (opacity) => {
          set({ wallpaperOpacity: opacity });
          syncWallpaperPreference({
            ...useGlobalStore.getState(),
            wallpaperOpacity: opacity,
          });
        },

        setWallpaperComponentBgTransparency: (transparency) => {
          const next = Math.min(100, Math.max(0, Math.round(transparency)));
          set({ wallpaperComponentBgTransparency: next });
          syncWallpaperPreference({
            ...useGlobalStore.getState(),
            wallpaperComponentBgTransparency: next,
          });
        },
      }),
      {
        name: "global-store",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => {
          return {
            theme: state.theme,
            language: state.language,
          };
        },
        // 回灌时忽略旧 global-store 中的壁纸字段，壁纸只认独立 preference
        merge: (persistedState, currentState) => {
          const persisted =
            persistedState && typeof persistedState === "object"
              ? (persistedState as Partial<GlobalStore>)
              : {};
          const appTheme = localStorage.getItem(
            THEME_STORAGE_KEY
          ) as ThemeType | null;

          return {
            ...currentState,
            // app-theme 为权威来源，避免子应用误写 global-store 后覆盖正确主题
            theme: appTheme ?? persisted.theme ?? currentState.theme,
            language: persisted.language ?? currentState.language,
          };
        },
      }
    ),
    { name: "GlobalStore" }
  )
);

/** 子应用 mount 时从主应用 globalState 同步偏好，须在 login 之前调用 */
export const syncHostPreferencesFromGlobalState = (globalState: {
  theme?: ThemeType;
  language?: string;
}): void => {
  const patch: Partial<Pick<GlobalStore, "theme" | "language">> = {};

  if (globalState.theme) {
    patch.theme = globalState.theme;
    setStoredTheme(globalState.theme);
  }
  if (globalState.language) {
    patch.language = globalState.language as LanguageType;
    if (globalState.language !== i18n.language) {
      void i18n.changeLanguage(globalState.language as LanguageType);
    }
  }
  if (Object.keys(patch).length > 0) {
    useGlobalStore.setState(patch);
  }
};

// 基本信息
export const useTheme = () => useGlobalStore((state) => state.theme);
export const useLanguage = () => useGlobalStore((state) => state.language);
export const useSetTheme = () => useGlobalStore((state) => state.setTheme);
export const useSetLanguage = () =>
  useGlobalStore((state) => state.setLanguage);
export const useSdkApi = () => useGlobalStore((state) => state.sdkApi);
export const useSetSdkApi = () => useGlobalStore((state) => state.setSdkApi);

export const useEquipmentData = () =>
  useGlobalStore((state) => state.equipmentData);
// 设备信息
export const useCurrentDeviceType = () =>
  useGlobalStore((state) => state.currentDeviceType);
export const useSetCurrentDeviceType = () =>
  useGlobalStore((state) => state.setCurrentDeviceType);
export const useCurrentDeviceSystem = () =>
  useGlobalStore((state) => state.currentDeviceSystem);
export const useSetCurrentDeviceSystem = () =>
  useGlobalStore((state) => state.setCurrentDeviceSystem);
export const useCurrentDeviceName = () =>
  useGlobalStore((state) => state.currentDeviceName);
export const useSetCurrentDeviceName = () =>
  useGlobalStore((state) => state.setCurrentDeviceName);
// 用户信息
export const useUserInfo = () => useGlobalStore((state) => state.userInfo);
export const useToken = () => useGlobalStore((state) => state.token);
export const useIsLoggedIn = () => useGlobalStore((state) => state.isLoggedIn);
export const useSetUserInfo = () =>
  useGlobalStore((state) => state.setUserInfo);
export const useSetToken = () => useGlobalStore((state) => state.setToken);
export const useLogin = () => useGlobalStore((state) => state.login);
export const useLogout = () => useGlobalStore((state) => state.logout);
export const useSetEquipmentData = () =>
  useGlobalStore((state) => state.setEquipmentData);
export const useGetAuthorizedDevices = () =>
  useGlobalStore((state) => state.authorizedDevices);
export const useSetAuthorizedDevices = () =>
  useGlobalStore((state) => state.setAuthorizedDevices);

// 壁纸 selectors
export const useWallpaperEnabled = () =>
  useGlobalStore((state) => state.wallpaperEnabled);
export const useWallpaperUrl = () =>
  useGlobalStore((state) => state.wallpaperUrl);
export const useWallpaperSource = () =>
  useGlobalStore((state) => state.wallpaperSource);
export const useWallpaperId = () =>
  useGlobalStore((state) => state.wallpaperId);
export const useWallpaperMediaType = () =>
  useGlobalStore((state) => state.wallpaperMediaType);
export const useWallpaperBlur = () =>
  useGlobalStore((state) => state.wallpaperBlur);
export const useWallpaperBrightness = () =>
  useGlobalStore((state) => state.wallpaperBrightness);
export const useWallpaperOpacity = () =>
  useGlobalStore((state) => state.wallpaperOpacity);
export const useSetWallpaperEnabled = () =>
  useGlobalStore((state) => state.setWallpaperEnabled);
export const useSetWallpaperImage = () =>
  useGlobalStore((state) => state.setWallpaperImage);
export const useSetWallpaperBlur = () =>
  useGlobalStore((state) => state.setWallpaperBlur);
export const useSetWallpaperBrightness = () =>
  useGlobalStore((state) => state.setWallpaperBrightness);
export const useSetWallpaperOpacity = () =>
  useGlobalStore((state) => state.setWallpaperOpacity);
export const useWallpaperComponentBgTransparency = () =>
  useGlobalStore((state) => state.wallpaperComponentBgTransparency);
export const useSetWallpaperComponentBgTransparency = () =>
  useGlobalStore((state) => state.setWallpaperComponentBgTransparency);
